/**
 * Admin VoD Management Page
 * Manage all uploaded VoDs - view, edit, delete
 */

import { useState } from 'react';
import { orderBy } from 'firebase/firestore';
import { useFirestoreCollection } from '../hooks/useFirestoreCollection';
import type { EventVodDocument } from '@schemas/firestore';

// Game options
const GAMES = [
    { id: 'all', name: 'All Games' },
    { id: 'smash-bros-solo', name: 'Smash Bros Solo' },
    { id: 'smash-bros-crews', name: 'Smash Bros Crews' },
    { id: 'rocket-league', name: 'Rocket League' },
    { id: 'mario-kart', name: 'Mario Kart' },
    { id: 'madden', name: 'Madden NFL' },
    { id: 'fortnite', name: 'Fortnite' },
];

interface VodWithId extends EventVodDocument {
    id: string;
}

export default function AdminVodsPage() {
    // Use the reusable Firestore hook
    const { data: vods, loading, remove } = useFirestoreCollection<VodWithId>({
        collectionName: 'eventVods',
        queryConstraints: [orderBy('createdAt', 'desc')],
    });

    const [gameFilter, setGameFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState<'all' | 'event' | 'opponent' | 'team' | 'general'>('all');
    const [deleting, setDeleting] = useState<string | null>(null);

    // Filter VoDs
    const filteredVods = vods.filter(vod => {
        const matchesGame = gameFilter === 'all' || vod.game === gameFilter;
        const matchesType = typeFilter === 'all' || vod.associationType === typeFilter;
        return matchesGame && matchesType;
    });

    // Delete VoD
    const handleDelete = async (vodId: string) => {
        if (!confirm('Are you sure you want to delete this VoD?')) return;

        setDeleting(vodId);
        try {
            await remove(vodId);
        } catch (err) {
            console.error('Error deleting VoD:', err);
            alert('Failed to delete VoD');
        } finally {
            setDeleting(null);
        }
    };

    // Get association badge
    const getTypeBadge = (vod: VodWithId) => {
        const styles: Record<string, string> = {
            event: 'bg-blue-500/20 text-blue-400',
            opponent: 'bg-yellow-500/20 text-yellow-400',
            team: 'bg-green-500/20 text-green-400',
            general: 'bg-secondary-500/20 text-secondary-400',
        };
        const icons: Record<string, string> = {
            event: '📅',
            opponent: '🎯',
            team: '👥',
            general: '📚',
        };
        return (
            <span className={`text-xs px-2 py-0.5 rounded ${styles[vod.associationType] || ''}`}>
                {icons[vod.associationType] || ''} {vod.associationType}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-5xl">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-white">📹 VoD Library</h1>
                    <p className="text-secondary-400">{vods.length} videos uploaded</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
                <select
                    value={gameFilter}
                    onChange={(e) => setGameFilter(e.target.value)}
                    className="px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white"
                >
                    {GAMES.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                </select>
                <div className="flex gap-1">
                    {(['all', 'event', 'opponent', 'team', 'general'] as const).map(type => (
                        <button
                            key={type}
                            onClick={() => setTypeFilter(type)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${typeFilter === type
                                ? 'bg-purple-500 text-white'
                                : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
                                }`}
                        >
                            {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* VoD Grid */}
            {filteredVods.length === 0 ? (
                <div className="card p-8 text-center">
                    <p className="text-secondary-400">No VoDs found</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredVods.map(vod => (
                        <div
                            key={vod.id}
                            className="card flex items-center justify-between gap-4"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-medium text-white truncate">{vod.title}</h3>
                                    {getTypeBadge(vod)}
                                </div>
                                <div className="flex items-center gap-3 text-sm text-secondary-400">
                                    <span>{GAMES.find(g => g.id === vod.game)?.name || vod.game}</span>
                                    <span>•</span>
                                    <span>by {vod.uploadedByName}</span>
                                    {vod.opponentName && (
                                        <>
                                            <span>•</span>
                                            <span className="text-yellow-400">vs {vod.opponentName}</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <a
                                    href={vod.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-secondary-400 hover:text-white hover:bg-secondary-700 rounded-lg transition-colors"
                                    title="Open Video"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </a>
                                <button
                                    onClick={() => handleDelete(vod.id)}
                                    disabled={deleting === vod.id}
                                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
                                    title="Delete"
                                >
                                    {deleting === vod.id ? (
                                        <div className="w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
